import { useCallback } from "react";
import {
  trackImageApproval,
  trackBackGenerationDecision,
} from "@/utils/analytics";
import { updateJourneyStage } from "@/utils/journeyTracking";
import type { DesignSpec } from "@/types/designSpec";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { markAgentSessionReset } from "../utils/agentSessionId";
import { ActionHookContext } from "./types";

export function useNavigationActions(
  context: ActionHookContext,
  generateImages: (
    targetStep?: number,
    isChatMode?: boolean,
    designSpecOverride?: DesignSpec
  ) => Promise<void>
) {
  const { store, stateRef, router, isChatMode } = context;

  const setStep = useCallback(
    (step: number) => {
      store.setStep(step);
    },
    [store]
  );

  const handleNext = useCallback(() => {
    const currentState = stateRef.current;

    // Prevent navigation during generation states
    if (
      currentState.generation.progress.loading ||
      currentState.ui.modification.isModifying ||
      currentState.generation.images.isSelectingVariation
    ) {
      return;
    }

    if (currentState.form.currentStep === 1) {
      // Validate front design (required)
      const isFrontUploadPreset = !!currentState.form.data.isFrontUploadPreset;
      const hasValidFrontPrompt =
        currentState.form.data.prompt.trim().length >= 10;
      const hasFrontUploadedImage = !!(
        currentState.form.data.uploadedFrontImages &&
        currentState.form.data.uploadedFrontImages.length > 0
      );

      const frontIsValid = isFrontUploadPreset
        ? hasFrontUploadedImage
        : hasValidFrontPrompt;

      if (!frontIsValid) {
        store.setValidationError(true);
        return;
      }

      store.setValidationError(false);
      // Move to step 2 and trigger front generation
      store.setStep(2);
      generateImages(2);
    } else if (currentState.form.currentStep === 2) {
      // Validate that a variation is selected if variations exist
      if (
        currentState.generation.images.variations.front.length > 0 &&
        currentState.generation.images.variations.front.length > 1
      ) {
        if (currentState.generation.images.selected.frontIndex === null) {
          store.setError("Choose a version before continuing.");
          return;
        }
      }
      // Validate that tickets exist for variations (should have been created immediately when variations were generated)
      if (
        currentState.generation.images.variations.front.length > 0 &&
        currentState.generation.images.variations.front.length > 0
      ) {
        const hasTickets =
          currentState.ticket.variationTicketIds &&
          Object.keys(currentState.ticket.variationTicketIds).length > 0;
        if (!hasTickets && !currentState.ticket.id) {
          store.setError("Wait for your design to finish saving.");
          return;
        }
      }
      // If validation passes, proceed to next step
      store.nextStep();
    } else if (currentState.form.currentStep === 3) {
      // Validate back prompt before proceeding
      const isBackUploadPreset = !!currentState.form.data.isBackUploadPreset;
      const hasValidBackPrompt =
        currentState.form.data.backPrompt &&
        currentState.form.data.backPrompt.trim().length >= 10;
      const hasBackUploadedImage = !!(
        currentState.form.data.uploadedBackImages &&
        currentState.form.data.uploadedBackImages.length > 0
      );

      const backIsValid = isBackUploadPreset
        ? hasBackUploadedImage
        : hasValidBackPrompt;

      if (!backIsValid) {
        store.setValidationError(true);
        return;
      }

      store.setValidationError(false);
      // Move to step 4 and trigger back generation
      store.setStep(4);
      generateImages(4);
    } else {
      store.nextStep();
    }
  }, [store, stateRef, generateImages]);

  const handlePrevious = useCallback(() => {
    const currentState = stateRef.current;
    if (currentState.form.currentStep > 1) {
      store.prevStep();
    }
  }, [store, stateRef]);

  const approveFrontDesign = useCallback(() => {
    const currentState = stateRef.current;

    // Prevent navigation during generation states
    if (
      currentState.generation.progress.loading ||
      currentState.ui.modification.isModifying ||
      currentState.generation.images.isSelectingVariation
    ) {
      return;
    }

    if (!currentState.generation.images.front.url) {
      store.setError("Create a front preview first.");
      return;
    }

    // Validate that a variation is selected if variations exist
    if (currentState.generation.images.variations.front.length > 1) {
      if (currentState.generation.images.selected.frontIndex === null) {
        store.setError("Choose a version before continuing.");
        return;
      }
    }

    // Validate that tickets exist for variations (should have been created immediately when variations were generated)
    if (currentState.generation.images.variations.front.length > 0) {
      const hasTickets =
        currentState.ticket.variationTicketIds &&
        Object.keys(currentState.ticket.variationTicketIds).length > 0;
      if (!hasTickets && !currentState.ticket.id) {
        store.setError("Wait for your design to finish saving.");
        return;
      }
    }

    // Track image approval
    trackImageApproval({
      image_type: "front",
      step_index: currentState.form.currentStep,
    });

    // Update journey stage
    updateJourneyStage("design", currentState.form.currentStep);

    store.approveFrontDesign();
  }, [store, stateRef]);

  const generateBackOnly = useCallback(
    (designSpecOverride?: DesignSpec) => {
      const currentState = stateRef.current;
      const frontUrl = currentState.generation.images.front.url;
      const frontPath = currentState.generation.images.front.path;

      if (!frontUrl) {
        store.setError(
          "We couldn't find your front preview. Please start again."
        );
        return;
      }

      if (!frontPath) {
        store.setError(
          "Wait for your front design to finish saving before creating the back."
        );
        return;
      }

      // Track back generation decision
      trackBackGenerationDecision({
        decision: "generate",
        step_index: currentState.form.currentStep,
      });

      store.setStep(4);
      generateImages(4, isChatMode, designSpecOverride);
    },
    [store, stateRef, generateImages, isChatMode]
  );

  const handleStartOver = useCallback(() => {
    // Stop any in-flight agent requests before clearing state
    store.abortActiveRequest();

    // Clear the active conversation id so the next send mints a fresh project.
    // The reset is RECORDED, not just applied: clearing the id
    // alone is indistinguishable from a browser that never had one, which is
    // exactly what the server-side restore fallback answers by handing back
    // the conversation the customer just ended.
    markAgentSessionReset();

    // Explicitly reset agent state before resetting everything
    store.clearConversation();
    store.setSessionId(null);
    store.setPhase("initial");
    store.setPresets([]);
    store.setTyping(false);
    store.setAgentError(null);

    // Reset all other state
    store.resetAll();

    // Clear only this browsing context's draft. Opaque URL-scoped auth,
    // quote, and credit handoffs may belong to another tab and must survive.
    FormPersistenceService.clearCanonicalState();
    router.push("/create");
  }, [store, router]);

  return {
    setStep,
    handleNext,
    handlePrevious,
    handleStartOver,
    approveFrontDesign,
    generateBackOnly,
  };
}
